import React from 'react';
import {
  FileText,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  FileAudio,
  FileVideo,
  File,
  X,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Clock,
  Ban,
} from 'lucide-react';

export interface UploadProgressCardProps {
  fileName: string;
  fileSize: number;
  progress: number; // 0-100
  status: 'uploading' | 'complete' | 'failed' | 'pending' | 'aborted';
  speed?: number; // bytes per second
  eta?: number; // seconds remaining
  onCancel?: () => void;
  onRetry?: () => void;
  variant?: 'default' | 'embedded';
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatSpeed(bytesPerSecond: number): string {
  if (bytesPerSecond === 0) return '0 KB/s';
  const k = 1024;
  if (bytesPerSecond < k) return `${bytesPerSecond.toFixed(0)} B/s`;
  if (bytesPerSecond < k * k) return `${(bytesPerSecond / k).toFixed(1)} KB/s`;
  return `${(bytesPerSecond / (k * k)).toFixed(1)} MB/s`;
}

function formatEta(seconds: number): string {
  if (seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function getFileIcon(fileName: string): React.ReactNode {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';

  const iconClass = 'h-8 w-8';

  if (['csv', 'xls', 'xlsx', 'tsv'].includes(ext)) {
    return <FileSpreadsheet className={`${iconClass} text-green-500 dark:text-green-400`} />;
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
    return <FileImage className={`${iconClass} text-purple-500 dark:text-purple-400`} />;
  }
  if (['zip', 'rar', 'tar', 'gz', '7z'].includes(ext)) {
    return <FileArchive className={`${iconClass} text-yellow-500 dark:text-yellow-400`} />;
  }
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma'].includes(ext)) {
    return <FileAudio className={`${iconClass} text-pink-500 dark:text-pink-400`} />;
  }
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) {
    return <FileVideo className={`${iconClass} text-blue-500 dark:text-blue-400`} />;
  }
  if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(ext)) {
    return <FileText className={`${iconClass} text-red-500 dark:text-red-400`} />;
  }
  return <File className={`${iconClass} text-gray-400 dark:text-gray-500`} />;
}

function getStatusIcon(status: UploadProgressCardProps['status']): React.ReactNode {
  switch (status) {
    case 'complete':
      return <CheckCircle2 className="h-5 w-5 text-green-500 dark:text-green-400" />;
    case 'failed':
      return <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />;
    case 'pending':
      return <Clock className="h-5 w-5 text-gray-400 dark:text-gray-500" />;
    case 'aborted':
      return <Ban className="h-5 w-5 text-gray-400 dark:text-gray-500" />;
    default:
      return null;
  }
}

function getProgressBarClasses(status: UploadProgressCardProps['status']): string {
  switch (status) {
    case 'uploading':
      return 'bg-blue-500 dark:bg-blue-400 animate-progress-stripe';
    case 'complete':
      return 'bg-green-500 dark:bg-green-400';
    case 'failed':
      return 'bg-red-500 dark:bg-red-400';
    case 'aborted':
      return 'bg-gray-400 dark:bg-gray-500';
    case 'pending':
    default:
      return 'bg-gray-300 dark:bg-gray-600';
  }
}

function getStatusLabel(status: UploadProgressCardProps['status']): string {
  switch (status) {
    case 'uploading':
      return 'Uploading';
    case 'complete':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    case 'aborted':
      return 'Aborted';
  }
}

export function UploadProgressCard({
  fileName,
  fileSize,
  progress,
  status,
  speed,
  eta,
  onCancel,
  onRetry,
  variant = 'default',
}: UploadProgressCardProps) {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className={variant === 'embedded' ? '' : 'rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800'}>
      <div className="flex items-start gap-3">
        {/* File icon */}
        <div className="flex-shrink-0">{getFileIcon(fileName)}</div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* File name and status */}
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
              {fileName}
            </p>
            <div className="flex items-center gap-2">
              {getStatusIcon(status)}
              {status === 'uploading' && onCancel && (
                <button
                  onClick={onCancel}
                  className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  aria-label="Cancel upload"
                  title="Cancel upload"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {status === 'failed' && onRetry && (
                <button
                  onClick={onRetry}
                  className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  aria-label="Retry upload"
                  title="Retry upload"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* File size and status label */}
          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatFileSize(fileSize)}</span>
            <span aria-hidden="true">&middot;</span>
            <span>{getStatusLabel(status)}</span>
            {status === 'uploading' && (
              <>
                {speed != null && speed > 0 && (
                  <>
                    <span aria-hidden="true">&middot;</span>
                    <span>{formatSpeed(speed)}</span>
                  </>
                )}
                {eta != null && eta > 0 && (
                  <>
                    <span aria-hidden="true">&middot;</span>
                    <span>{formatEta(eta)} remaining</span>
                  </>
                )}
              </>
            )}
          </div>

          {/* Progress bar */}
          <div className="mt-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all duration-300 ease-out ${getProgressBarClasses(status)}`}
                style={{ width: `${clampedProgress}%` }}
                role="progressbar"
                aria-valuenow={clampedProgress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            {status === 'uploading' && (
              <p className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
                {clampedProgress.toFixed(0)}%
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
